import { useEffect, useMemo, useState } from "react";
import { Input, Modal, Select } from "../../../shared";
import {
  useAddTemplateMutation,
  useUpdateTemplateMutation,
} from "../../../redux/services/templates/api";
import { useGetTaskTemplateGroupsQuery } from "../../../redux/services/tasks/tasksApi";
import { toast } from "react-toastify";
import { useConfig } from "../../../hooks";
import {
  LEG_STATUS_OPTIONS,
  LOAD_TYPE_OPTIONS,
  MILEAGE_SELECTION_OPTIONS,
  TEMPLATE_TYPES,
} from "./template.constants";
import { buildTemplatePayload, getTemplateContent } from "./template.utils";

const defaultForm = {
  name: "",
  description: "",
  loadType: "Regular",
  status: "pending",
  weight: "",
  appointmentTime: "",
  useLegFlatRate: false,
  baseCharge: "",
  fuelSurcharge: "",
  dropCharge: "",
  otherCharges: "",
  totalMiles: "",
  defaultMileage: "",
  customMileage: "",
  billedMiles: "",
  mileageSelectionType: "defaultMileage",
  useCustomMileage: false,
  useCustomBaseCharge: false,
  useCustomFuelSurcharge: false,
  useCustomDropCharge: false,
  useCustomOtherCharges: false,
  hasHandoff: false,
  isHandoff: false,
};

const LegTemplateModal = ({ visible, template, onClose, mode = "create" }) => {
  const { appType } = useConfig();
  const [form, setForm] = useState(defaultForm);
  const [selectedGroups, setSelectedGroups] = useState([]);

  const { data: groups = [] } = useGetTaskTemplateGroupsQuery({
    appliesTo: "leg",
  });
  const [addTemplate, { isLoading: isAdding }] = useAddTemplateMutation();
  const [updateTemplate, { isLoading: isUpdating }] =
    useUpdateTemplateMutation();

  const isLoading = isAdding || isUpdating;
  const isEdit = mode === "edit" && template?._id;

  useEffect(() => {
    if (!visible) return;

    const content = getTemplateContent(template);
    const defaults = content.defaults || content;

    setForm({
      ...defaultForm,
      name:
        mode === "duplicate" && template?.name
          ? `Copy of ${template.name}`
          : template?.name || "",
      description: template?.description || "",
      loadType: defaults.loadType || "Regular",
      status: defaults.status || "pending",
      weight: defaults.weight || "",
      appointmentTime: defaults.appointmentTime || "",
      useLegFlatRate: !!defaults.useLegFlatRate,
      baseCharge: defaults.baseCharge || "",
      fuelSurcharge: defaults.fuelSurcharge || "",
      dropCharge: defaults.dropCharge || "",
      otherCharges: defaults.otherCharges || "",
      totalMiles: defaults.totalMiles || "",
      defaultMileage: defaults.defaultMileage || "",
      customMileage: defaults.customMileage || "",
      billedMiles: defaults.billedMiles || "",
      mileageSelectionType:
        defaults.mileageSelectionType || "defaultMileage",
      useCustomMileage: !!defaults.useCustomMileage,
      useCustomBaseCharge: !!defaults.useCustomBaseCharge,
      useCustomFuelSurcharge: !!defaults.useCustomFuelSurcharge,
      useCustomDropCharge: !!defaults.useCustomDropCharge,
      useCustomOtherCharges: !!defaults.useCustomOtherCharges,
      hasHandoff: !!defaults.hasHandoff,
      isHandoff: !!defaults.isHandoff,
    });
    setSelectedGroups(content.taskTemplateGroups || []);
  }, [visible, template, mode]);

  const groupOptions = useMemo(
    () =>
      groups.filter((group) => {
        const appliesToMatch = ["leg", "both"].includes(
          group.appliesTo || "both"
        );
        const appTypeMatch =
          !group.appType || group.appType === "all" || group.appType === appType;
        return appliesToMatch && appTypeMatch;
      }),
    [groups, appType]
  );

  const handleChange = (id, value) => {
    setForm((prev) => ({ ...prev, [id]: value }));
  };

  const handleToggleGroup = (groupKey) => {
    setSelectedGroups((prev) =>
      prev.includes(groupKey)
        ? prev.filter((key) => key !== groupKey)
        : [...prev, groupKey]
    );
  };

  const handleSubmit = async () => {
    if (!form.name?.trim()) {
      toast.error("Template name is required");
      return;
    }

    const defaults = {
      loadType: form.loadType,
      status: form.status,
      weight: Number(form.weight) || 0,
      appointmentTime: form.appointmentTime,
      useLegFlatRate: form.useLegFlatRate,
      baseCharge: Number(form.baseCharge) || 0,
      fuelSurcharge: Number(form.fuelSurcharge) || 0,
      dropCharge: Number(form.dropCharge) || 0,
      otherCharges: Number(form.otherCharges) || 0,
      totalMiles: Number(form.totalMiles) || 0,
      defaultMileage: Number(form.defaultMileage) || 0,
      customMileage: Number(form.customMileage) || null,
      billedMiles: Number(form.billedMiles) || null,
      mileageSelectionType: form.mileageSelectionType,
      useCustomMileage: form.useCustomMileage,
      useCustomBaseCharge: form.useCustomBaseCharge,
      useCustomFuelSurcharge: form.useCustomFuelSurcharge,
      useCustomDropCharge: form.useCustomDropCharge,
      useCustomOtherCharges: form.useCustomOtherCharges,
      hasHandoff: form.hasHandoff,
      isHandoff: form.isHandoff,
    };

    const payload = buildTemplatePayload({
      type: TEMPLATE_TYPES.LEG,
      form,
      defaults,
      taskTemplateGroups: selectedGroups,
      appType,
    });

    try {
      const response = isEdit
        ? await updateTemplate({ id: template._id, data: payload }).unwrap()
        : await addTemplate(payload).unwrap();

      if (response.status !== "success") {
        toast.error("Failed to save template");
        return;
      }

      toast.success("Template saved successfully");
      onClose?.();
    } catch (error) {
      console.error("Save leg template error:", error);
      toast.error(error.data?.message || "Failed to save template");
    }
  };

  return (
    <Modal
      title={
        isEdit
          ? `Edit ${appType === "trucking" ? "Load Leg" : "Leg"} Template`
          : `Create ${appType === "trucking" ? "Load Leg" : "Leg"} Template`
      }
      visible={visible}
      onClose={onClose}
      confirm={handleSubmit}
      loading={isLoading}
      size="lg"
      submitText="Save Template"
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Template Name"
            value={form.name}
            onChange={(e) => handleChange("name", e.value)}
            placeholder="Standard pickup leg"
            required
          />
          <Input
            label="Description"
            value={form.description}
            onChange={(e) => handleChange("description", e.value)}
            placeholder="When this template should be used"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Load Type"
            value={form.loadType}
            options={LOAD_TYPE_OPTIONS}
            onChange={(e) => handleChange("loadType", e.value)}
          />
          <Select
            label="Status"
            value={form.status}
            options={LEG_STATUS_OPTIONS}
            onChange={(e) => handleChange("status", e.value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Input
            label="Weight"
            type="number"
            value={form.weight}
            onChange={(e) => handleChange("weight", e.value)}
          />
          <Input
            label="Appointment Time"
            value={form.appointmentTime}
            onChange={(e) => handleChange("appointmentTime", e.value)}
            placeholder="8:00 AM"
          />
          <Select
            label="Mileage Type"
            value={form.mileageSelectionType}
            options={MILEAGE_SELECTION_OPTIONS}
            onChange={(e) => handleChange("mileageSelectionType", e.value)}
          />
          <Input
            label="Total Miles"
            type="number"
            value={form.totalMiles}
            onChange={(e) => handleChange("totalMiles", e.value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Input
            label="Base Charge"
            type="number"
            value={form.baseCharge}
            onChange={(e) => handleChange("baseCharge", e.value)}
          />
          <Input
            label="Fuel Surcharge"
            type="number"
            value={form.fuelSurcharge}
            onChange={(e) => handleChange("fuelSurcharge", e.value)}
          />
          <Input
            label="Drop Charge"
            type="number"
            value={form.dropCharge}
            onChange={(e) => handleChange("dropCharge", e.value)}
          />
          <Input
            label="Other Charges"
            type="number"
            value={form.otherCharges}
            onChange={(e) => handleChange("otherCharges", e.value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            ["useLegFlatRate", "Use leg flat rate"],
            ["useCustomMileage", "Use custom mileage"],
            ["useCustomBaseCharge", "Custom base charge"],
            ["useCustomFuelSurcharge", "Custom fuel surcharge"],
            ["useCustomDropCharge", "Custom drop charge"],
            ["useCustomOtherCharges", "Custom other charges"],
            ["hasHandoff", "Has handoff"],
            ["isHandoff", "Is handoff"],
          ].map(([id, label]) => (
            <label key={id} className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={!!form[id]}
                onChange={(e) => handleChange(id, e.target.checked)}
              />
              {label}
            </label>
          ))}
        </div>

        {groupOptions.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-700">
              Task/Checklist Groups
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {groupOptions.map((group) => (
                <label key={group._id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedGroups.includes(group.groupKey)}
                    onChange={() => handleToggleGroup(group.groupKey)}
                  />
                  {group.title}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default LegTemplateModal;
